import { yupResolver } from '@hookform/resolvers/yup';
import { Divider, ToggleButtonGroup, Typography } from '@mui/material';
import { Resolver, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Trans } from 'react-i18next';

import { getAssetPath } from '@/constants/configs.ts';
import { MAX_LIABILITIES } from '@/constants/finances';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectDefaultCurrency } from '@/redux/configs/baseSelectors';

import { FinancialType } from '@/types/finance';

import { FinanceQuestion, useSubmitLiabilities } from '../../hooks';
import {
  AddBlockButton,
  FinancesQuestionForm,
  StyledToggleButton,
} from '../common';
import { LiabilitiesTotalLine } from './LiabilitiesTotalLine';
import { LiabilityBlock } from './LiabilityBlock';
import { LiabilityFormValues } from './type';
import { getLiabilitiesValidationSchema } from './validationSchema';

type QuestionLiabilitiesProps = {
  onSubmit: () => void;
};

export function QuestionLiabilities({
  onSubmit,
}: Readonly<QuestionLiabilitiesProps>) {
  const { t } = useTranslate();

  const { currency, currencyDisplay } = useAppSelector(selectDefaultCurrency);

  const { mutate, isPending } = useSubmitLiabilities({
    onSuccess: onSubmit,
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = useForm<LiabilityFormValues>({
    defaultValues: {
      liabilities: [{ liabilityType: '', amount: '' }],
    },
    mode: 'onChange',
    resolver: yupResolver(
      getLiabilitiesValidationSchema(t),
    ) as Resolver<LiabilityFormValues>,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'liabilities',
  });

  const hasLiability = useWatch({ control, name: 'hasLiability' });

  const handleAddLiability = () => {
    if (fields.length < MAX_LIABILITIES) {
      append({ liabilityType: '', amount: '' });
    }
  };

  const handleDeleteLiability = (index: number) => {
    remove(index);
  };

  const onContinue = handleSubmit(data => {
    if (hasLiability === false) {
      mutate({ hasLiability: false, liabilities: [] });
      return;
    }
    const payload = data.liabilities.map(liability => ({
      financialType: FinancialType.Liability,
      financialSubtype: liability.liabilityType,
      amount: Number(liability.amount),
      currency,
    }));
    mutate({ hasLiability: true, liabilities: payload });
  });

  const onChangeHasLiability = (_: unknown, val: boolean) => {
    if (val !== null) setValue('hasLiability', val);
  };
  const isContinueEnabled =
    hasLiability === false || (hasLiability === true && isValid);

  return (
    <FinancesQuestionForm
      question={FinanceQuestion.Liabilities}
      title={t('moreInfo.form.yourFinances.liabilities.title')}
      imageUrl={getAssetPath('/assets/images/icon-liabilities.svg')}
      onContinue={onContinue}
      disable={!isContinueEnabled}
      isLoading={isPending}
      subtitle={
        <Trans
          i18nKey="moreInfo.form.yourFinances.liabilities.subtitle"
          components={{
            bold: (
              <Typography
                component="span"
                variant="body2Bold"
                color="neutral.1000"
              />
            ),
          }}
        />
      }
    >
      <ToggleButtonGroup
        value={hasLiability}
        exclusive
        onChange={onChangeHasLiability}
        fullWidth
        sx={{ mb: 3 }}
      >
        <StyledToggleButton value={true}>{t('common.yes')}</StyledToggleButton>
        <StyledToggleButton value={false}>{t('common.no')}</StyledToggleButton>
      </ToggleButtonGroup>

      {hasLiability === true && (
        <>
          {fields.map((field, index) => (
            <LiabilityBlock
              key={field.id}
              index={index}
              total={fields.length}
              currencyDisplay={currencyDisplay}
              control={control}
              onDelete={() => handleDeleteLiability(index)}
            />
          ))}

          {fields.length < MAX_LIABILITIES && (
            <AddBlockButton
              label={t('moreInfo.form.yourFinances.liabilities.addLiability')}
              onClick={handleAddLiability}
            />
          )}

          <Divider sx={{ mb: 2 }} />

          <LiabilitiesTotalLine
            control={control}
            currencyDisplay={currencyDisplay}
          />
        </>
      )}
    </FinancesQuestionForm>
  );
}
